import { useQuery } from '@apollo/client';
import { faClockRotateLeft, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { format, formatDistanceStrict } from 'date-fns';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { withIcon } from '../../../../core/components/Icon.tsx';
import TablePagination from '../../../../core/components/TablePagination.tsx';
import classnames from '../../../../core/utils/classnames.tsx';
import { GET_AGENT_PROCESSING_RECORD, GET_AGENT_PROCESSING_RECORDS } from '../../queries.ts';
import ProcessingRecordModal, { parseJsonSafe } from '../records/ProcessingRecordModal';
import { getRecordStatusInfo } from '../records/recordStatus.ts';

const RunIcon = withIcon(faClockRotateLeft);
const LoadingIcon = withIcon(faSpinnerThird);

const DEFAULT_ITEMS_PER_PAGE = 10;

interface TestRunsListProps {
    agentId: string;
    refetchKey: number;
}

/** An agent's test runs, as on the Agents page's Testing tab, in a list that fits a drawer. */
const TestRunsList = ({ agentId, refetchKey }: TestRunsListProps) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    const [activePage, setActivePage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
    const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);

    const { data, loading, refetch } = useQuery(GET_AGENT_PROCESSING_RECORDS, {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        variables: {
            agentId,
            offset: (activePage - 1) * itemsPerPage,
            limit: itemsPerPage,
            isTesting: true,
            filters: [],
        },
    });

    useEffect(() => {
        if (refetchKey > 0) refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetchKey]);

    const { data: selectedRecordData } = useQuery(GET_AGENT_PROCESSING_RECORD, {
        variables: { id: selectedRecordId },
        skip: !selectedRecordId,
    });
    const selectedRecord = selectedRecordData?.agentProcessingRecord;

    const records = data?.filteredAgentProcessingRecordsList?.records || [];
    const totalCount = data?.filteredAgentProcessingRecordsList?.totalCount ?? 0;

    if (!records.length) {
        return loading ? (
            <div className="flex justify-center py-5">
                <LoadingIcon spin className="text-lg text-content-medium" />
            </div>
        ) : (
            <div className="flex flex-col gap-1 text-sm text-content-medium">
                <span className="font-medium">{t('historyPage.noData.title')}</span>
                <span>{t('historyPage.noData.message')}</span>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <ul className={classnames('m-0 flex list-none flex-col p-0', loading && 'opacity-50')}>
                {records.map((record: any) => {
                    const inputData = parseJsonSafe(record.inputData);
                    const status = getRecordStatusInfo(record, t);
                    const createdBy = record.createdBy
                        ? `${record.createdBy.firstName || ''} ${record.createdBy.lastName || ''}`.trim()
                        : '';
                    const meta = [
                        inputData?.sender,
                        createdBy,
                        format(new Date(record.createdAt), 'PPp'),
                        record.finishedAt
                            ? formatDistanceStrict(
                                  new Date(record.stepRun?.createdAt || record.createdAt),
                                  new Date(record.finishedAt)
                              )
                            : '-',
                    ].filter(Boolean);
                    return (
                        <li key={record.id}>
                            <button
                                type="button"
                                onClick={() => setSelectedRecordId(record.id)}
                                className="flex w-full cursor-pointer items-center gap-3 rounded border-none bg-transparent px-2 py-2.5 text-left transition-colors hover:bg-surface-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong"
                            >
                                <RunIcon className="flex-none text-content-medium" />
                                <span className="flex min-w-0 flex-1 flex-col">
                                    <span className="truncate text-sm font-medium text-content-strong">
                                        {inputData?.subject || '-'}
                                    </span>
                                    <span className="truncate text-xs text-content-medium">{meta.join(' · ')}</span>
                                </span>
                                <span
                                    className={classnames(
                                        'inline-flex flex-none items-center rounded border border-solid px-2 py-1 text-xs font-medium',
                                        status.colorClass
                                    )}
                                >
                                    {status.label}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>

            {totalCount > itemsPerPage && (
                <TablePagination
                    totalCount={totalCount}
                    activePage={activePage}
                    itemsPerPage={itemsPerPage}
                    setActivePage={setActivePage}
                    setItemsPerPage={setItemsPerPage}
                />
            )}

            {selectedRecord && (
                <ProcessingRecordModal
                    visible
                    onClose={() => setSelectedRecordId(null)}
                    record={selectedRecord}
                    isTesting
                />
            )}
        </div>
    );
};

export default TestRunsList;
